import React from "react";
import { Funnel } from "lucide-react";
import GenericButton from "@ui-components/Button/GenericButton";
import { LogItem } from "./LogsData";
import { parseLogAttributes } from "./logAttributes";
import { CTXR_FIELD_LABEL, TIMESTAMP_FIELD_LABEL } from "./logFieldSpec";
import { useCopyFeedback } from "./useCopyFeedback";

/** Adds `label: value` to Search Log. `label` must name a field of `makeLogFields`. */
export type FilterByAttribute = (label: string, value: string) => void;

interface LogRowDetailsProps {
  row: LogItem;
  /** The CTX-R the logs were fetched for, as the CTX-R filter names it. */
  ctxr: string;
  onFilter: FilterByAttribute;
}

/**
 * What Copy JSON puts on the clipboard: the row as the handler sent it, so the
 * timestamp is the raw one, not the local time the table shows.
 */
export function logRowJson(row: LogItem, ctxr: string): string {
  return JSON.stringify(
    { timestamp: row.rawTimestamp, level: row.level, message: row.message, ctxr },
    null,
    2
  );
}

// Same label treatment as Reflector V2's LogEntryModal.
const SECTION_LABEL_CLASS = "text-xs uppercase tracking-wide text-semantic-text-secondary";

function CopyTextButton({ label, text }: { label: string; text: string }) {
  const { copied, copy } = useCopyFeedback();
  return (
    <GenericButton
      label={copied ? "Copied" : label}
      onClick={() => copy(text)}
      variant="secondary"
      size="sm"
    />
  );
}

interface AttributeProps {
  label: string;
  value: string;
  onFilter: FilterByAttribute;
}

// An empty value gets no button: an empty chip filters nothing.
function Attribute({ label, value, onFilter }: AttributeProps) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <dt className={SECTION_LABEL_CLASS}>{label}</dt>
      <dd className="flex items-center gap-1 text-sm text-semantic-text-primary">
        <span className="min-w-0 break-words">{value || "—"}</span>
        {value && (
          <GenericButton
            onClick={() => onFilter(label, value)}
            icon={<Funnel className="w-3.5 h-3.5 text-semantic-text-secondary" aria-hidden />}
            isIconOnly
            variant="secondary"
            shape="ghost"
            size="xsm"
            className="flex-shrink-0"
            title={`Filter by ${label}`}
            aria-label={`Filter by ${label}`}
          />
        )}
      </dd>
    </div>
  );
}

/**
 * The expanded log row: attributes on the left, the original message on the
 * right. The attributes are the ones parsed out of the message (CTX-1356),
 * then the CTX-R and the timestamp; each one's filter button adds it to Search
 * Log. The message is rendered as text, never as HTML.
 */
export function LogRowDetails({ row, ctxr, onFilter }: LogRowDetailsProps) {
  return (
    <div className="my-2 grid grid-cols-1 gap-4 rounded-md border border-semantic-border-secondary bg-semantic-bg-primary-alt p-4 md:grid-cols-[minmax(200px,1fr)_3fr]">
      <section aria-label="Attributes">
        <h4 className={`mb-1 ${SECTION_LABEL_CLASS}`}>Attributes</h4>
        <dl>
          {parseLogAttributes(row.message).map(({ key, label, value }) => (
            <Attribute key={key} label={label} value={value} onFilter={onFilter} />
          ))}
          <Attribute label={CTXR_FIELD_LABEL} value={ctxr} onFilter={onFilter} />
          <Attribute label={TIMESTAMP_FIELD_LABEL} value={row.timestamp} onFilter={onFilter} />
        </dl>
      </section>

      <section aria-label="Original message" className="min-w-0">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <h4 className={SECTION_LABEL_CLASS}>Original message</h4>
          <div className="flex gap-2">
            <CopyTextButton label="Copy" text={row.message} />
            <CopyTextButton label="Copy JSON" text={logRowJson(row, ctxr)} />
          </div>
        </div>
        <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-semantic-border-primary bg-semantic-bg-primary p-3 font-mono text-xs text-semantic-text-primary">
          {row.message}
        </pre>
      </section>
    </div>
  );
}
